import { useEffect, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RichEditor from "@/components/admin/RichEditor";
import { ImageUpload, GalleryUpload, PdfUpload } from "@/components/admin/Uploaders";

const empty = {
    title: "",
    title_en: "",
    author: "",
    category: "written",
    subject: "",

    description: "",
    description_en: "",

    specification: "",
    specification_en: "",

    price: 0,
    discount_price: null,

    cover_image: "",
    gallery_images: [],

    sample_pdf_url: "",
    sample_pdf_text: "",
    full_pdf_url: "",

    stock: 0,
    is_featured: false,

    pages: 0,
    publisher: "Self Preparation",
    isbn: "",

    order: 0,

    subscription_plans: [],
};

const SUBJECTS = [
    "গণিত",
    "ইংরেজি",
    "বাংলা",
    "সাধারণ জ্ঞান",
    "বিজ্ঞান",
    "ICT/কম্পিউটার",
    "ইতিহাস",
    "ভূগোল",
    "অর্থনীতি",
    "মনোবিজ্ঞান",
    "অন্যান্য"
];

export default function AdminBooks() {
    const [books, setBooks] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editId, setEditId] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () =>
        api.get("/admin/books").then((r) => setBooks(r.data));

    useEffect(() => {
        load();
    }, []);

    const openNew = () => {
        setForm(empty);
        setEditId(null);
        setOpen(true);
    };

    const openEdit = (b) => {
        setForm({
            ...empty,
            ...b,
            gallery_images: b.gallery_images || [],
            order: b.order || 0,
            subscription_plans: b.subscription_plans || [],
        });

        setEditId(b.id);
        setOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();

        setBusy(true);

        try {
            const payload = {
                ...form,

                price: Number(form.price),

                discount_price:
                    form.discount_price !== null &&
                        form.discount_price !== ""
                        ? Number(form.discount_price)
                        : null,

                stock: Number(form.stock),

                pages: Number(form.pages),

                order: Number(form.order || 0),

                subscription_plans: (form.subscription_plans || [])
                    .map((p) => ({
                        days: Number(p.days) || 0,
                        price: Number(p.price) || 0,
                        label: p.label || "",
                    }))
                    .filter((p) => p.days > 0),
            };

            if (editId) {
                await api.put(`/admin/books/${editId}`, payload);
            } else {
                await api.post("/admin/books", payload);
            }

            toast.success("Saved");

            setOpen(false);

            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const del = async (id) => {
        if (!confirm("Delete this book?")) return;

        try {
            await api.delete(`/admin/books/${id}`);

            toast.success("Deleted");

            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const setF = (k, v) =>
        setForm((p) => ({
            ...p,
            [k]: v,
        }));

    const fullUrl = (u) =>
        u?.startsWith("/")
            ? `${BACKEND_URL}${u}`
            : u;

    console.log("books -- ", books);

    return (
        <div data-testid="admin-books-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-brand-navy">
                        Books
                    </h1>

                    <p className="text-sm text-brand-navy/55">
                        Manage your book inventory
                    </p>
                </div>

                <button
                    onClick={openNew}
                    className="btn-primary px-4 py-2 rounded-lg font-semibold inline-flex items-center gap-2"
                    data-testid="add-book-btn"
                >
                    <Plus className="w-4 h-4" />
                    Add Book
                </button>
            </div>

            <div className="bg-white rounded-2xl border border-brand-light overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-brand-light/40 text-brand-navy">
                        <tr>
                            <th className="text-left px-4 py-3">
                                Cover
                            </th>

                            <th className="text-left px-4 py-3">
                                Title
                            </th>

                            <th className="text-left px-4 py-3">
                                Cat / Subject
                            </th>

                            <th className="text-left px-4 py-3">
                                Price
                            </th>
                            
                            <th className="text-left px-4 py-3">
                                Stock
                            </th>


                            <th className="text-left px-4 py-3">
                                Featured
                            </th>

                            <th className="text-left px-4 py-3">
                                Order
                            </th>

                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>

                    <tbody>
                        {books.map((b) => (
                            <tr
                                key={b.id}
                                className="border-t border-brand-light hover:bg-brand-light/15"
                            >
                                <td className="px-4 py-3">
                                    <img
                                        src={fullUrl(b.cover_image)}
                                        alt=""
                                        className="w-10 h-12 rounded object-cover"
                                    />
                                </td>

                                <td className="px-4 py-3 font-medium text-brand-navy max-w-xs truncate">
                                    {b.title}
                                </td>

                                <td className="px-4 py-3 text-xs">
                                    {b.category}
                                    {b.subject
                                        ? ` / ${b.subject}`
                                        : ""}
                                    {b.category === "ebook" && (
                                        <span className={`ml-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${b.full_pdf_url ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                                            {b.full_pdf_url ? "PDF ✓" : "No PDF"}
                                        </span>
                                    )}
                                </td>

                                <td className="px-4 py-3">
                                    ৳{b.discount_price || b.price}
                                </td>

                                <td
                                    className="px-4 py-3 font-medium"
                                    style={{
                                        color:
                                            b.stock < 10
                                                ? "#D61F1F"
                                                : "#08145A",
                                    }}
                                >
                                    {b.stock}
                                </td>

                                <td className="px-4 py-3">
                                    {b.is_featured ? "✓" : ""}
                                </td>

                                <td className="px-4 py-3">
                                    {b.order || 0}
                                </td>

                                <td className="px-4 py-3">
                                    <div className="flex gap-1 justify-end">
                                        <button
                                            onClick={() => openEdit(b)}
                                            className="p-1.5 hover:bg-brand-light/50 rounded"
                                            data-testid={`edit-book-${b.id}`}
                                        >
                                            <Edit className="w-3.5 h-3.5" />
                                        </button>

                                        <button
                                            onClick={() => del(b.id)}
                                            className="p-1.5 hover:bg-brand-red/10 text-brand-red rounded"
                                            data-testid={`del-book-${b.id}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto font-bn">
                    <DialogHeader>
                        <DialogTitle>
                            {editId ? "Edit Book" : "New Book"}
                        </DialogTitle>
                    </DialogHeader>

                    <form
                        onSubmit={save}
                        className="grid grid-cols-1 md:grid-cols-3 gap-4"
                    >
                        <div className="md:col-span-1 space-y-4">
                            <ImageUpload
                                value={form.cover_image}
                                onChange={(v) =>
                                    setF("cover_image", v)
                                }
                                label="Cover Image"
                            />

                            <GalleryUpload
                                value={form.gallery_images}
                                onChange={(v) =>
                                    setF("gallery_images", v)
                                }
                            />

                            <PdfUpload
                                value={form.sample_pdf_url}
                                onChange={(v) =>
                                    setF("sample_pdf_url", v)
                                }
                            />

                            {form.category === "ebook" && (
                                <FullPdfUpload
                                    value={form.full_pdf_url}
                                    onChange={(v) => setF("full_pdf_url", v)}
                                />
                            )}
                        </div>

                        <div className="md:col-span-2 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <Inp
                                    label="Title (BN)"
                                    value={form.title}
                                    onChange={(v) =>
                                        setF("title", v)
                                    }
                                    required
                                />

                                <Inp
                                    label="Title (EN)"
                                    value={form.title_en}
                                    onChange={(v) =>
                                        setF("title_en", v)
                                    }
                                />

                                <Inp
                                    label="Author"
                                    value={form.author}
                                    onChange={(v) =>
                                        setF("author", v)
                                    }
                                />

                                <div>
                                    <label className="block text-xs mb-1 font-medium">
                                        Category *
                                    </label>

                                    <Select
                                        value={form.category}
                                        onValueChange={(v) =>
                                            setF("category", v)
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>

                                        <SelectContent>
                                            <SelectItem value="written">
                                                Written
                                            </SelectItem>

                                            <SelectItem value="mcq">
                                                MCQ
                                            </SelectItem>

                                            <SelectItem value="job-solution">
                                                Job Solution
                                            </SelectItem>

                                            <SelectItem value="ebook">
                                                E-Book
                                            </SelectItem>

                                            <SelectItem value="offline">
                                                Offline Only
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <label className="block text-xs mb-1 font-medium">
                                        Subject
                                    </label>

                                    <Select
                                        value={
                                            form.subject || "__none"
                                        }
                                        onValueChange={(v) =>
                                            setF(
                                                "subject",
                                                v === "__none"
                                                    ? ""
                                                    : v
                                            )
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Subject" />
                                        </SelectTrigger>

                                        <SelectContent>
                                            <SelectItem value="__none">
                                                — None —
                                            </SelectItem>

                                            {SUBJECTS.map((s) => (
                                                <SelectItem
                                                    key={s}
                                                    value={s}
                                                >
                                                    {s}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <Inp
                                    label="Price (৳)"
                                    type="number"
                                    value={form.price}
                                    onChange={(v) =>
                                        setF("price", v)
                                    }
                                    required
                                />

                                <Inp
                                    label="Discount Price"
                                    type="number"
                                    value={
                                        form.discount_price ?? ""
                                    }
                                    onChange={(v) =>
                                        setF(
                                            "discount_price",
                                            v
                                        )
                                    }
                                />

                                <Inp
                                    label="Stock"
                                    type="number"
                                    value={form.stock}
                                    onChange={(v) =>
                                        setF("stock", v)
                                    }
                                />

                                <Inp
                                    label="Order"
                                    type="number"
                                    value={form.order}
                                    onChange={(v) =>
                                        setF("order", v)
                                    }
                                />

                                <Inp
                                    label="Pages"
                                    type="number"
                                    value={form.pages}
                                    onChange={(v) =>
                                        setF("pages", v)
                                    }
                                />

                                <Inp
                                    label="ISBN"
                                    value={form.isbn}
                                    onChange={(v) =>
                                        setF("isbn", v)
                                    }
                                />

                                <Inp
                                    label="Publisher"
                                    value={form.publisher}
                                    onChange={(v) =>
                                        setF("publisher", v)
                                    }
                                />
                            </div>

                            <div>
                                <label className="block text-xs mb-1 font-medium">
                                    Description (BN) — Rich Text
                                </label>

                                <RichEditor
                                    value={form.description}
                                    onChange={(v) =>
                                        setF("description", v)
                                    }
                                />
                            </div>

                            <div>
                                <label className="block text-xs mb-1 font-medium">
                                    Specification — Rich Text
                                </label>

                                <RichEditor
                                    value={form.specification}
                                    onChange={(v) =>
                                        setF(
                                            "specification",
                                            v
                                        )
                                    }
                                />
                            </div>

                            <details className="text-sm">
                                <summary className="cursor-pointer font-medium text-brand-royal">
                                    English fields & sample text
                                    fallback
                                </summary>

                                <div className="mt-2 space-y-3">
                                    <div>
                                        <label className="block text-xs mb-1 font-medium">
                                            Description (EN)
                                        </label>

                                        <RichEditor
                                            value={
                                                form.description_en
                                            }
                                            onChange={(v) =>
                                                setF(
                                                    "description_en",
                                                    v
                                                )
                                            }
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs mb-1 font-medium">
                                            Specification (EN)
                                        </label>

                                        <RichEditor
                                            value={
                                                form.specification_en
                                            }
                                            onChange={(v) =>
                                                setF(
                                                    "specification_en",
                                                    v
                                                )
                                            }
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs mb-1 font-medium">
                                            Sample text (used if no
                                            PDF)
                                        </label>

                                        <textarea
                                            rows={4}
                                            value={
                                                form.sample_pdf_text ||
                                                ""
                                            }
                                            onChange={(e) =>
                                                setF(
                                                    "sample_pdf_text",
                                                    e.target.value
                                                )
                                            }
                                            className="w-full px-3 py-2 rounded-lg border border-brand-light font-bn"
                                        />
                                    </div>
                                </div>
                            </details>

                            {form.category === "ebook" && (
                                <SubscriptionPlansEditor
                                    value={form.subscription_plans}
                                    onChange={(v) =>
                                        setF("subscription_plans", v)
                                    }
                                />
                            )}

                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.is_featured}
                                    onChange={(e) =>
                                        setF(
                                            "is_featured",
                                            e.target.checked
                                        )
                                    }
                                />

                                Featured on homepage
                            </label>

                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    className="px-4 py-2 rounded-lg border border-brand-light"
                                >
                                    Cancel
                                </button>

                                <button
                                    disabled={busy}
                                    className="btn-primary px-5 py-2 rounded-lg font-semibold disabled:opacity-60"
                                >
                                    {busy
                                        ? "Saving..."
                                        : "Save"}
                                </button>
                            </div>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Inp({
    label,
    value,
    onChange,
    type = "text",
    required,
}) {
    return (
        <div>
            <label className="block text-xs mb-1 font-medium">
                {label}
                {required && " *"}
            </label>

            <input
                type={type}
                value={value ?? ""}
                required={required}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-brand-light bg-white focus:outline-none focus:ring-2 focus:ring-brand-royal/30"
            />
        </div>
    );
}

// ─── Full PDF Upload Component ─────────────────────────────────────────────────
function FullPdfUpload({ value, onChange }) {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.type !== "application/pdf") {
            setError("শুধু PDF file দিন");
            return;
        }
        if (file.size > 50 * 1024 * 1024) {
            setError("PDF সর্বোচ্চ 50MB হতে পারবে");
            return;
        }
        setError("");
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await api.post("/admin/upload?kind=pdf", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(res.data.url);
            toast.success("Full PDF uploaded!");
        } catch (err) {
            setError(formatApiError(err) || "Upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    return (
        <div className="border-2 border-dashed border-amber-400 rounded-xl p-3 bg-amber-50 space-y-2">
            <label className="block text-xs font-semibold text-amber-700">
                📄 Full Paid PDF (ই-বুক)
            </label>
            <p className="text-xs text-amber-600">
                Payment confirm হলে user এই PDF দেখতে পাবে • সর্বোচ্চ 50MB
            </p>

            {value ? (
                <div className="flex items-center gap-2 bg-white border border-green-300 rounded-lg px-3 py-2">
                    <span className="text-green-600 text-sm font-semibold flex-1 truncate">✓ PDF uploaded</span>
                    <a
                        href={value}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-blue-600 underline shrink-0"
                    >
                        Preview
                    </a>
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        className="text-xs text-red-500 hover:text-red-700 shrink-0"
                    >
                        ✕ Remove
                    </button>
                </div>
            ) : (
                <label className={`flex items-center justify-center gap-2 cursor-pointer border border-amber-300 rounded-lg px-3 py-2.5 bg-white hover:bg-amber-50 transition ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                    {uploading ? (
                        <>
                            <span className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                            <span className="text-xs text-amber-700">Uploading...</span>
                        </>
                    ) : (
                        <>
                            <span className="text-amber-600 text-lg">📤</span>
                            <span className="text-xs text-amber-700 font-medium">PDF ফাইল বেছে নিন</span>
                        </>
                    )}
                    <input
                        type="file"
                        accept=".pdf,application/pdf"
                        className="hidden"
                        onChange={handleFile}
                        disabled={uploading}
                    />
                </label>
            )}

            {error && <p className="text-xs text-red-600 font-medium">⚠ {error}</p>}
        </div>
    );
}

// ─── Subscription Plans Editor (per ebook pricing) ─────────────────────────────
function SubscriptionPlansEditor({ value, onChange }) {
    const plans = value || [];

    const update = (idx, key, val) => {
        const next = plans.map((p, i) =>
            i === idx ? { ...p, [key]: val } : p
        );
        onChange(next);
    };

    const addPlan = () => {
        onChange([...plans, { days: 7, price: 0, label: "৭ দিন" }]);
    };

    const removePlan = (idx) => {
        onChange(plans.filter((_, i) => i !== idx));
    };

    return (
        <div className="border-2 border-dashed border-brand-royal/40 rounded-xl p-3 bg-brand-royal/5 space-y-2">
            <div className="flex items-center justify-between">
                <label className="block text-xs font-semibold text-brand-royal">
                    💳 সাবস্ক্রিপশন প্ল্যান (এই বইয়ের জন্য)
                </label>

                <button
                    type="button"
                    onClick={addPlan}
                    className="text-xs px-2 py-1 rounded-lg bg-brand-royal text-white hover:bg-brand-royal/90 inline-flex items-center gap-1"
                >
                    <Plus className="w-3 h-3" />
                    প্ল্যান যুক্ত করুন
                </button>
            </div>

            <p className="text-xs text-brand-navy/60">
                এই বইটি পড়ার জন্য ইউজার কোন মেয়াদে কত টাকায় সাবস্ক্রাইব করতে পারবে তা ঠিক করুন। গ্লোবাল পেমেন্ট সেটিংসে আর মূল্য সেট করা লাগবে না — এখানে যা দেবেন সেটাই এই বইয়ের জন্য প্রযোজ্য হবে।
            </p>

            {plans.length === 0 && (
                <p className="text-xs text-amber-600 font-medium">
                    ⚠ কোনো প্ল্যান যুক্ত করা হয়নি — যুক্ত না করলে ইউজার এই বইয়ের সাবস্ক্রিপশন নিতে পারবে না।
                </p>
            )}

            {plans.map((p, idx) => (
                <div
                    key={idx}
                    className="grid grid-cols-12 gap-2 items-end bg-white border border-brand-light rounded-lg p-2"
                >
                    <div className="col-span-3">
                        <label className="block text-[10px] mb-1 text-brand-navy/60">
                            দিন
                        </label>

                        <input
                            type="number"
                            min={1}
                            value={p.days}
                            onChange={(e) =>
                                update(idx, "days", e.target.value)
                            }
                            className="w-full px-2 py-1.5 rounded-lg border border-brand-light text-sm"
                        />
                    </div>

                    <div className="col-span-3">
                        <label className="block text-[10px] mb-1 text-brand-navy/60">
                            মূল্য (৳)
                        </label>

                        <input
                            type="number"
                            min={0}
                            value={p.price}
                            onChange={(e) =>
                                update(idx, "price", e.target.value)
                            }
                            className="w-full px-2 py-1.5 rounded-lg border border-brand-light text-sm"
                        />
                    </div>

                    <div className="col-span-5">
                        <label className="block text-[10px] mb-1 text-brand-navy/60">
                            লেবেল
                        </label>

                        <input
                            type="text"
                            value={p.label}
                            placeholder="যেমন: ৭ দিন"
                            onChange={(e) =>
                                update(idx, "label", e.target.value)
                            }
                            className="w-full px-2 py-1.5 rounded-lg border border-brand-light text-sm font-bn"
                        />
                    </div>

                    <div className="col-span-1 flex justify-end">
                        <button
                            type="button"
                            onClick={() => removePlan(idx)}
                            title="Remove plan"
                            className="p-1.5 text-brand-red hover:bg-brand-red/10 rounded-lg"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}
